import io from 'socket.io-client';
import NotificationModel from 'Models/NotificationModel';
import { Events } from 'Models/ChatModel';

export const SOCKET_NOTIFICATIONS = 'notifications';
export const SOCKET_CHAT = 'chat';

class SocketAdapter {
  constructor(options) {
    const boosterEventsObject = Object.fromEntries(NotificationModel.BOOSTER_EVENTS_LIST.map((e) => [e.title, []]));
    const adminEventsObject = Object.fromEntries(NotificationModel.ADMIN_EVENTS_LIST.map((e) => [e.title, []]));
    const clientEventsObject = Object.fromEntries(NotificationModel.CLIENT_EVENTS_LIST.map((e) => [e.title, []]));

    // socket.io-client as provider
    this.providers = {
      [SOCKET_NOTIFICATIONS]: io(options.notificationsUrl, { transports: ['websocket'] }),
      [SOCKET_CHAT]: io(options.chatUrl, options.chatOptions),
    };

    this.events = {
      [SOCKET_NOTIFICATIONS]: {
        new_order: [],
        order_custom_sale_payed: [],
        booster_new_order: [],
        ...adminEventsObject,
        ...boosterEventsObject,
        ...clientEventsObject,
      },

      [SOCKET_CHAT]: {
        [Events.JOINED]: [],
        [Events.LEAVED]: [],
        [Events.MESSAGE]: [],
        [Events.NEW_MESSAGE]: [],
        [Events.MESSAGE_STATE]: [],
        [Events.HISTORY]: [],
        [Events.USERS_LIST]: [],
        // [Events.USER_STATUS]: [],
        [Events.CONNECT_ERROR]: [],
        [Events.RECONNECT]: [],
      },
    };

    // default flag for room is 'default'
    this.rooms = {
      [SOCKET_NOTIFICATIONS]: [],
      [SOCKET_CHAT]: [],
    };

    this.registerProviderEvents();
    this.handleDisconnect();
  }

  addListener(provider, event, callback) {
    if (!this.events[provider][event]) {
      return SocketAdapter
        .returnError(`Wrong event name, was expected one of this: ${Object.keys(this.events[provider]).join(', ')}`);
    }

    if (typeof callback !== 'function') {
      return null;
    }

    if (!this.hasListener(provider, event, callback)) {
      this.events[provider][event].push(callback);
    }

    return event;
  }

  listenOnce(provider, event, callback) {
    const handler = (e) => {
      callback(e);
      this.removeListener(provider, event, handler);
    };

    this.addListener(provider, event, handler);
  }

  removeListener(provider, event, callback) {
    if (!this.events[provider][event]) {
      return SocketAdapter
        .returnError(`Wrong event name, was expected one of this: ${Object.keys(this.events[provider]).join(', ')}`);
    }

    this.events[provider][event] = this.events[provider][event].filter((action) => action !== callback);

    return event;
  }

  removeNamespaceListeners(provider, event) {
    if (event) {
      if (!this.events[provider][event]) {
        return SocketAdapter
          .returnError(`Wrong event name, was expected one of this: ${Object.keys(this.events[provider]).join(', ')}`);
      }

      this.events[provider][event] = [];
    } else if (this.events[provider]) {
      this.events[provider] = {};
    }

    return event || provider;
  }

  hasListener(provider, event, callback) {
    if (this.events[provider][event]) {
      return Boolean(this.events[provider][event].find((action) => action === callback));
    }

    return false;
  }

  sendMessage(provider, event, data) {
    // socket.io-client emit
    this.providers[provider].emit(event, data);
  }

  /**
   * отправляет join команду в socket.io
   * присоединяется к переданным комнатам
   * комнатам можно указать флаг для группировки
   * по флагу можно потом выйти из всех комнат в этой группе
   * @see leaveRoomsByFlag
   *
   * @param {String} provider один из this.providers
   * @param {Array} data массив объектов с информацией о комнатах
   * пример: [{ name: 'my-room', flag: 'admin' }, { name: 'private-777' }]
   */
  joinRooms(provider, data) {
    // plain array with room names (strings)
    const roomsToJoin = [];

    // filling rooms object
    data.forEach((room) => {
      if (typeof room !== 'object' && !room.name) {
        // eslint-disable-next-line no-console
        console.error('Room should be an object with name field');
        return;
      }

      const newRoom = room;
      if (!newRoom.flag) {
        newRoom.flag = 'default';
      }

      this.rooms[provider].push(newRoom);
      roomsToJoin.push(newRoom.name);
    });

    this.sendMessage(provider, 'join', roomsToJoin);
  }

  /**
   * отправляет leave сообщение в socket.io
   * покидает все переданные комнаты по названию
   *
   * @param {String} provider один из this.providers
   * @param {Array} data массив названий комнат
   * пример: ['my-room', 'private-777']
   */
  leaveRoomsByName(provider, data) {
    this.rooms[provider] = this.rooms[provider].filter((room) => !data.includes(room.name));
    this.sendMessage(provider, 'leave', data);
  }

  /**
   * отправляет leave сообщение в socket.io
   * покидает все комнаты по переданным флагам
   *
   * @param {String} provider один из this.providers
   * @param {Array} data массив флагов комнат
   * пример: ['admin', 'booster']
   */
  leaveRoomsByFlag(provider, data) {
    const newRooms = [];
    const roomsToLeave = [];

    this.rooms[provider].forEach((room) => {
      if (data.includes(room.flag)) {
        roomsToLeave.push(room.name);
      } else {
        newRooms.push(room);
      }
    });

    this.rooms[provider] = newRooms;
    this.sendMessage(provider, 'leave', roomsToLeave);
  }

  emitCallbacks(provider, event, data) {
    if (this.events[provider][event].length > 0) {
      this.events[provider][event].forEach((callback) => {
        callback(data);
      });
    }
  }

  registerProviderEvents() {
    Object.keys(this.events).forEach((provider) => {
      const events = this.events[provider];
      Object.keys(events).forEach((eventName) => {
        this.providers[provider].on(eventName, (data) => {
          this.emitCallbacks(provider, eventName, data);
        });
      });
    });
  }

  handleDisconnect() {
    this.providers[SOCKET_NOTIFICATIONS].on('reconnect', () => {
      // Заново присоединяемся к комнатам нотифаев
      // Реконнект к комнатам чата происходит тут resources/assets/js/classes/ClientChat.class.js:33
      const roomsToReconnect = this.rooms[SOCKET_NOTIFICATIONS].map((room) => room.name);
      if (roomsToReconnect.length) {
        this.sendMessage(SOCKET_NOTIFICATIONS, 'join', roomsToReconnect);
      }
    });
  }

  static returnError(message) {
    return new Error(message);
  }

  /**
   * Подключается к сокету по namespace
   * Сразу после подключения кидает в него переданные данные, если они есть
   * @param {String} provider один из this.providers
   * @param {?Object=} dataToPass данные которые надо передать на сервер socket.io
   */
  connectToProvider(provider, dataToPass) {
    if (this.providers[provider].connected) {
      return;
    }

    this.providers[provider].connect();

    if (dataToPass) {
      this.providers[provider].on('connect', () => {
        this.sendMessage(provider, 'connectData', dataToPass);
      });
    }
  }

  /**
   * Отключается от сокета по namespace
   * @param {String} provider один из this.providers
   */
  disconnectFromProvider(provider) {
    this.providers[provider].disconnect(true);
  }
}

export default SocketAdapter;
